// Custom implementation of https://github.com/mustaphaturhan/chakra-ui-markdown-renderer
import React from 'react';
import deepmerge from 'deepmerge';
import { Components } from 'react-markdown';
import {
  chakra,
  Checkbox,
  Code,
  Divider,
  Heading,
  Image,
  Link,
  ListItem,
  OrderedList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList
} from '@chakra-ui/react';

type GetCoreProps = {
  children?: React.ReactNode;
  'data-sourcepos'?: any;
};

function getCoreProps(props: GetCoreProps): any {
  return props['data-sourcepos'] ? { 'data-sourcepos': props['data-sourcepos'] } : {};
}

interface Defaults extends Components {
  /**
   * @deprecated Use `h1, h2, h3, h4, h5, h6` instead.
   */
  heading?: Components['h1'];
}

export const defaults: Defaults = {
  p: ({ children }) => (
    <Text mb={4} fontSize='lg'>
      {children}
    </Text>
  ),
  em: (props) => {
    const { children } = props;
    return <Text as='em'>{children}</Text>;
  },
  blockquote: (props) => {
    const { children } = props;
    return (
      <Code as='blockquote' p={2}>
        {children}
      </Code>
    );
  },
  code: (props) => {
    // @ts-ignore
    const { inline, children, className } = props;

    if (inline) {
      return <Code p={2}>{children}</Code>;
    }

    return (
      <Code className={className} whiteSpace='break-spaces' display='block' w='full' p={2}>
        {children}
      </Code>
    );
  },
  del: (props) => {
    const { children } = props;
    return <Text as='del'>{children}</Text>;
  },
  hr: () => {
    return <Divider />;
  },
  a: Link,
  img: Image,
  text: (props) => {
    const { children } = props;
    return <Text as='span'>{children}</Text>;
  },
  ul: (props) => {
    // TODO: TS_error
    // @ts-ignore
    const { ordered, children, depth } = props;
    const attrs = getCoreProps(props);
    let Element = UnorderedList;
    let styleType = 'disc';
    if (ordered) {
      Element = OrderedList;
      styleType = 'decimal';
    }
    if (depth === 1) styleType = 'circle';
    return (
      <Element spacing={2} as={ordered ? 'ol' : 'ul'} styleType={styleType} pl={4} mb={6} {...attrs}>
        {children}
      </Element>
    );
  },
  ol: (props) => {
    // TODO: TS_error
    // @ts-ignore
    const { ordered, children, depth } = props;
    const attrs = getCoreProps(props);
    let Element = UnorderedList;
    let styleType = 'disc';
    if (ordered) {
      Element = OrderedList;
      styleType = 'decimal';
    }
    if (depth === 1) styleType = 'circle';
    return (
      <Element spacing={2} as={ordered ? 'ol' : 'ul'} styleType={styleType} pl={4} mb={6} {...attrs}>
        {children}
      </Element>
    );
  },
  li: (props) => {
    // TODO: TS_error
    // @ts-ignore
    const { children, checked } = props;
    let checkbox = null;
    if (checked !== null && checked !== undefined) {
      checkbox = (
        <Checkbox isChecked={checked} isReadOnly ml={-8}>
          {children}
        </Checkbox>
      );
    }
    return (
      <ListItem {...getCoreProps(props)} listStyleType={checked !== null ? 'none' : 'inherit'}>
        {checkbox || children}
      </ListItem>
    );
  },
  // @ts-ignore
  input: ({ children }) => children,
  h2: ({ children }) => (
    <Heading as='h2' mt={10} mb={2} size='md' fontSize='3xl' fontWeight='extrabold'>
      {children}
    </Heading>
  ),
  // eslint-disable-next-line react/no-unstable-nested-components
  h3: ({ children }) => (
    <Heading as='h3' mt={8} mb={2} size='sm' fontSize='2xl' fontWeight='extrabold'>
      {children}
    </Heading>
  ), // eslint-disable-next-line react/no-unstable-nested-components
  h4: ({ children }) => (
    <Heading as='h4' mt={6} mb={2} size='xs' fontSize='xl' fontWeight='bold'>
      {children}
    </Heading>
  ),
  heading: (props) => {
    // TODO: TS_error
    // @ts-ignore
    const { level, children } = props;
    const sizes = ['2xl', 'xl', 'lg', 'md', 'sm', 'xs'];
    return (
      <Heading my={4} as={`h${level}`} size={sizes[`${level - 1}`]} {...getCoreProps(props)}>
        {children}
      </Heading>
    );
  },
  pre: (props) => {
    const { children } = props;
    return <chakra.pre {...getCoreProps(props)}>{children}</chakra.pre>;
  },
  // table: Table,
  table: ({ children }) => (
    <TableContainer mb={6} border='1px' borderColor='bg-default' bg='bg-surface' borderRadius='2xl' boxShadow='sm'>
      <Table variant='simple'>{children}</Table>
    </TableContainer>
  ),
  thead: Thead,
  tbody: Tbody,
  tr: (props) => <Tr>{props.children}</Tr>,
  td: (props) => <Td>{props.children}</Td>,
  th: (props) => <Th>{props.children}</Th>
};

function ChakraUIRenderer(theme?: Defaults, merge = true): Components {
  const elements = {
    p: defaults.p,
    em: defaults.em,
    blockquote: defaults.blockquote,
    code: defaults.code,
    del: defaults.del,
    hr: defaults.hr,
    a: defaults.a,
    img: defaults.img,
    text: defaults.text,
    ul: defaults.ul,
    ol: defaults.ol,
    li: defaults.li,
    input: defaults.input,
    h1: defaults.heading,
    h2: defaults.h2,
    h3: defaults.h3,
    h4: defaults.h4,
    h5: defaults.heading,
    h6: defaults.heading,
    pre: defaults.pre,
    table: defaults.table,
    thead: defaults.thead,
    tbody: defaults.tbody,
    tr: defaults.tr,
    td: defaults.td,
    th: defaults.th
  };

  if (theme && merge) {
    return deepmerge(elements, theme);
  }

  return elements;
}

export default ChakraUIRenderer;
